import axios from "axios"
import { loginSuccess, logout } from "../../store/authSlice"
import { saveAs } from "file-saver"

// const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8800" // local backend
// const API_URL = process.env.NEXT_PUBLIC_API_URL || "https://app-03-cb-api-dev.azurewebsites.net" // dev backend
const API_URL = process.env.NEXT_PUBLIC_API_URL || "https://app-05-cb-api-pre-prod-a3e2arcfbehcgbha.uksouth-01.azurewebsites.net" // pre-prodbackend

let jwtToken = null
let refreshToken = null

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

apiClient.interceptors.request.use(
  (config) => {
    const token = jwtToken || localStorage.getItem("jwtToken")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      console.error("No token found")
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    console.debug(error.response?.status, originalRequest.url)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        const newTokens = await refreshAccessToken()
        jwtToken = newTokens.accessToken
        localStorage.setItem("jwtToken", jwtToken)
        originalRequest.headers.Authorization = `Bearer ${jwtToken}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        console.error("Error refreshing token:", refreshError?.response?.data?.msg || refreshError.message)
        clearState()
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login?final=" + window.location.pathname
        }
        return Promise.reject(refreshError)
      }
    } else if (error.response?.status === 401) {
      clearState()
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login?final=" + window.location.pathname
      }
    }
    return Promise.reject(error)
  }
)

const refreshAccessToken = async () => {
  try {
    const response = await axios.post(`${API_URL}/refresh-token`, {
      refreshToken: refreshToken || localStorage.getItem("refreshToken"),
    })
    return {
      accessToken: response.data.accessToken,
    }
  } catch (error) {
    console.error("Error refreshing access token:", error)
    throw error
  }
}

export const login = async (email, password, dispatch) => {
  try {
    const response = await axios.post(`${API_URL}/login`, {
      customer_email: email,
      password,
    })
    jwtToken = response.data.accessToken
    refreshToken = response.data.refreshToken
    localStorage.setItem("jwtToken", jwtToken)
    localStorage.setItem("refreshToken", refreshToken)

    let data = response.data
    if (data.user.client_id) data.user.active = 1
    else data.user.active = 0
    dispatch(loginSuccess(data))
    return data
  } catch (error) {
    console.error("Error logging in:", error)
    throw error
  }
}

export const loginAdmin = async (token, dispatch) => {
  try {
    jwtToken = token
    localStorage.setItem("jwtToken", jwtToken)
    const response = await axios.post(`${API_URL}/login/admin`, {
      token,
    })
    
    let data = response.data
    if (data.user.client_id) data.user.active = 1
    else data.user.active = 0

    data.accessToken = token

    dispatch(loginSuccess(data))
    return data
  } catch (error) {
    console.error("Error logging in:", error)
    throw error
  }
}

export const clearState = () => {
  jwtToken = null
  refreshToken = null
  localStorage.removeItem("jwtToken")
  localStorage.removeItem("refreshToken")
  localStorage.removeItem("userData")
}

export const getSites = async (params = {}) => {
  try {
    const response = await apiClient.get(`/sites`, { params })
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getUsers = async () => {
  try {
    const response = await apiClient.get(`/user`)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getInvoices = async (config = {}) => {
  try {
    const response = await apiClient.get(`/invoices`, config)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getSchedules = async () => {
  try {
    const response = await apiClient.get(`/schedule`)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getSite = async () => {
  try {
    const response = await apiClient.get(`/sites`)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getBookings = async (params) => {
  try {
    const response = await apiClient.get(`/bookings`, { params })
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getSessionTransaction = async (params) => {
  try {
    const response = await apiClient.get(`/session/transaction`, { params })
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getSession = async (params) => {
  try {
    const response = await apiClient.get(`/session/active`, { params })
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const attemptAction = async (action, booking_id) => {
  try {
    const response = await apiClient.patch(`/session/active/${action}`, { booking_id })
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getBookingAggs = async (params) => {
  try {
    const response = await apiClient.get(`/bookings/count`, { params })
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const fetchPastUtilisation = async () => {
  try {
    const response = await apiClient.get("/bookings/utilisation")
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const updateBooking = async (booking_id, data) => {
  try {
    const response = await apiClient.patch(`/booking/` + booking_id, data)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const fetchChargerAvailability = async () => {
  try {
    const response = await apiClient.get(`/charger/availability/schedules`)
    return response.data
  } catch (error) {
    console.error("Error fetching charger availability:", error)
    throw error
  }
}

export const getClients = async () => {
  try {
    const response = await apiClient.get(`/clients`)
    return response.data
  } catch (error) {
    console.error("Error fetching clients list:", error)
    throw error
  }
}

export const getOwnerChargers = async (params) => {
  try {
    const response = await apiClient.get(`/chargers`, { params })
    return response.data
  } catch (error) {
    console.error("Error fetching chargers list:", error)
    throw error
  }
}

export const getCustomers = async () => {
  try {
    const response = await apiClient.get(`/permissions/customer/client`)
    return response.data
  } catch (error) {
    console.error("Error fetching customers list:", error)
    throw error
  }
}

export const getCustomerDiscounts = async () => {
  try {
    const response = await apiClient.get('/customer-discounts')
    return response.data
  } catch (error) {
    throw error
  }
}

export const getCustomerGroups = async () => {
  try {
    const response = await apiClient.get(`/customer-groups`)
    return response.data
  } catch (error) {
    console.error("Error fetching customers list:", error)
    throw error
  }
}

export const getSiteBookings = async (data) => {
  try {
    const response = await apiClient.get(`/bookings/availability`, {
      params: data,
    })
    return response.data
  } catch (error) {
    console.error("Error fetching customers list:", error)
    throw error
  }
}

export const updateCustomerPermissions = async (data) => {
  try {
    const response = await apiClient.patch(`/permissions/customer/client`, data)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const updateInvoice = async (data) => {
  try {
    const response = await apiClient.patch(`/invoice`, data)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const updateSite = async (siteId, data) => {
  try {
    const response = await apiClient.patch(`/site/${siteId}`, data)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}
export const updateCharger = async (chargerId, data, accessToken) => {
  try {
    if (data.schedule_uid === "NOT_LISTED") {
      data.schedule_uid = null
    }
    const response = await apiClient.patch("/chargers/" + chargerId, {
      charger_alias: data.charger_alias,
      booking_active: true,
      schedule_uid: data.schedule_uid,
      charger_instructions: data.charger_instructions,
      bookingAction: data.bookingAction,
      min_cost_h: data.min_cost_h,
      min_booking_period: data.min_booking_period,
      max_booking_period: data.max_booking_period,
      min_cancellation_notice_h: data.min_cancellation_notice_h,
      size_restriction: data.size_restriction,
    })

    // const response = await apiClient.patch(`/chargers/${chargerId}`, {
    //   method: "PATCH",
    //   headers: {
    //     "Content-Type": "application/json",
    //     Authorization: `Bearer ${accessToken}`,
    //   },
    //   body: data,
    // })

    //if (!response.ok) {
    //  throw new Error("Failed to update charger")
    //}

    return response.data
  } catch (error) {
    console.error("Error updating charger:", error)
    throw error
  }
}

export const createBooking = async (data) => {
  try {
    const response = await apiClient.post("/booking", data)
    return response.data
  } catch (error) {
    throw new Error("Failed to create booking: " + (error.response?.data?.msg || error.message))
  }
}

export const createTariff = async (data) => {
  if (data.schedule_uid === "") {
    delete data.schedule_uid
  }
  try {
    const response = await apiClient.post("/schedule", data)
    return response.data
  } catch (error) {
    throw new Error("Failed to create booking: " + (error.response?.data?.msg || error.message))
  }
}

export const updateTariff = async (uid, data) => {
  data.schedule_uid = uid
  try {
    const response = await apiClient.patch(`/schedule/`, data)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const deleteTariff = async (uid) => {
  try {
    const response = await apiClient.delete(`/schedule/${uid}`)
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const requestCustomerPermission = async (data) => {
  try {
    const response = await apiClient.post("/permissions/customer/client", data)
    return response.data
  } catch (error) {
    throw new Error("Failed to create booking: " + (error.response?.data?.msg || error.message))
  }
}

export const exportInvoices = async (config = {}) => {
  try {
    const response = await apiClient.get(`/invoices`, config)
    const fileData = response.data

    // Create a Blob from the file data
    const blob = new Blob([fileData], { type: "application/octet-stream" })

    // Create a file name for the downloaded file
    const fileName = "billing.csv" // Change the file extension based on your needs

    // Trigger the download using file-saver
    saveAs(blob, fileName)

    return response.data
  } catch (error) {
    throw new Error("Failed to create booking: " + (error.response?.data?.msg || error.message))
  }
}

export const inviteCustomer = async (data) => {
  try {
    const response = await apiClient.post("/customer/register", data)
    return response.data
  } catch (error) {
    throw new Error("Failed to invite customer: " + (error.response?.data?.msg || error.message))
  }
}

export const getRegistrationDetails = async (token) => {
  try {
    const response = await apiClient.get("/customer/registration/details", { params: { token } })
    return response.data
  } catch (error) {
    error.message = "Failed to invite customer: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const registerCustomer = async (data) => {
  try {
    const response = await apiClient.patch("/customer/accept", data)
    return response.data
  } catch (error) {
    throw new Error("Failed to invite customer: " + (error.response?.data?.msg || error.message))
  }
}

export const createCustomerGroup = async (data) => {
  try {
    const response = await apiClient.post("/customer-group", data)
    return response.data
  } catch (error) {
    throw new Error("Failed to create customer group: " + (error.response?.data?.msg || error.message))
  }
}

export const getCustomer = async (id) => {
  try {
    const response = await apiClient.get("/customer/" + id)
    return response.data
  } catch (error) {
    error.message = "Failed to fetch customer: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const getCustomerGroup = async (id) => {
  try {
    const response = await apiClient.get("/customer-group/" + id)
    return response.data
  } catch (error) {
    error.message = "Failed to fetch customer: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const updateCustomerGroup = async (id, data) => {
  try {
    const response = await apiClient.patch("/customer-group/" + id, data)
    return response.data
  } catch (error) {
    error.message = "Failed to create customer group: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const updateCustomerToGroup = async (customer_id, group_id) => {
  try {
    const response = await apiClient.patch(`/customer/group`, { customer_id, group_id })
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const updateBookingsBulk = async (booking_ids, action, customer_id) => {
  try {
    const response = await apiClient.patch(`/bookings/bulk`, { booking_ids, action, customer_id })
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getOrganisationDetails = async () => {
  try {
    const response = await apiClient.get("/customer")
    return response.data
  } catch (error) {
    error.message = "Failed to get organisation details: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const updateOrganisationDetails = async (data) => {
  try {
    const response = await apiClient.patch("/customer", data)
    return response.data
  } catch (error) {
    error.message = "Failed to update organisation details: " + error.message + ` (${error.response?.data?.msg})`
    throw error // new Error()
  }
}

export const getRFIDCards = async ({ id, site_id } = {}) => {
  try {
    const params = {}
    if (id) {
      params.id = id
    }
    if (site_id) {
      params.site_id = site_id
    }
    const response = await apiClient.get("/rfids", { params })
    return response.data
  } catch (error) {
    console.error("Error fetching RFID cards: ", error)
    error.message = "Failed to get rfid cards: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const updateRFIDCard = async (id, data) => {
  try {
    const response = await apiClient.patch("/rfid/" + id, data)
    return response
  } catch (error) {
    error.message = "Failed to update rfid card: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const createRFIDCard = async (data) => {
  try {
    const response = await apiClient.post("/rfid", data)
    return response.data
  } catch (error) {
    error.message = "Failed to create a new RFID card: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const deleteRFIDCard = async (id) => {
  try {
    const response = await apiClient.delete(`/rfid/${id}`)
    return response.data
  } catch (error) {
    error.message = "Failed to delete RFID card: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const getClientSettings = async () => {
  try {
    const response = await apiClient.get("/client/settings/")
    return response.data
  } catch (error) {
    error.message = "Failed to fetch settings: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const updateClientSettings = async (data) => {
  try {
    const response = await apiClient.patch("/client/settings/", data)
    return response.data
  } catch (error) {
    error.message = "Failed to update settings: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const getTeamMembers = async (id) => {
  try {
    const params = {}
    if (id) {
      params.customer_user_id = id
    }
    const response = await apiClient.get(`/users`, { params })
    return response.data
  } catch (error) {
    error.message = "Failed to get team members: " + (error.response?.data?.msg || error.message)
    throw error // new Error()
  }
}

export const createNewUser = async (data) => {
  try {
    const response = await apiClient.post("/user/register", data)
    return response.data
  } catch (error) {
    error.message = "Failed to create new user: " + error.message + ` (${error.response?.data?.msg})`
    throw error // new Error()
  }
}

export const updateUser = async (id, data) => {
  try {
    const response = await apiClient.patch(`/user/${id}`, data)
    return response.data
  } catch (error) {
    throw new Error("Failed to update user: " + error.message + ` (${error.response?.data?.msg})`)
  }
}

export const registerUser = async (data) => {
  try {
    const response = await apiClient.patch("/user/accept", data)
    return response.data
  } catch (error) {
    throw new Error("Failed to invite customer: " + error.message + ` (${error.response?.data?.msg})`)
  }
}

export const requestPasswordReset = async (email) => {
  try {
    const response = await apiClient.post("/password/request", { email })
    return response.data
  } catch (error) {
    throw new Error("Failed to request password reset: " + (error.response?.data?.msg || error.message))
  }
}

export const resetPassword = async (data) => {
  try {
    const response = await apiClient.post("/password/reset", data)
    return response.data
  } catch (error) {
    throw new Error("Failed to reset password: " + (error.response?.data?.msg || error.message))
  }
}

export const _apiClient = apiClient

export const logoutUser = (dispatch) => {
  clearState()
  dispatch(logout())
}

export const uploadSiteInstructions = async (file, siteId) => {
  try {
    const formData = new FormData()
    formData.append("file", file)
    const response = await apiClient.post(`/site/${siteId}/instructions`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  } catch (error) {
    console.error("Error uploading site instructions:", error)
    throw error
  }
}

export const checkSiteInstructionsExists = async (siteId) => {
  try {
    const response = await apiClient.get(`/site/${siteId}/instructions`)
    return response.data
  } catch (error) {
    console.error("Error checking site instructions:", error)
    throw error
  }
}

export const deleteSiteInstructions = async (siteId) => {
  try {
    const response = await apiClient.delete(`/site/${siteId}/instructions`)
    return response.data
  } catch (error) {
    console.error("Error deleting site instructions:", error)
    throw error
  }
}

export const disputeBooking = async (data) => {
  try {
    const response = await apiClient.post("/booking/dispute", data)
    return response.data
  } catch (error) {
    console.error("Error disputing booking:", error)
    throw error
  }
}

export const createNewClientRequest = async (data) => {
  try {
    const response = await axios.post(`${API_URL}/admin/new-client-request`, data)
    return response.data
  } catch (error) {
    console.error("Error creating new client request:", error)
    throw error
  }
}
